import { Alert, Button, Input, Space, Tabs } from 'antd'
import { ReloadOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons'
import { useState, useEffect, useMemo } from 'react'
import { useAuth } from '../../../auth/AuthProvider'
import { isSysAdmin, isWorkspaceAdmin, isWorkspaceApprover, isAnyRequester } from '../../../utils/roles'
import { Loading } from '../../../components/Loading'
import { EmptyState } from '../../../components/EmptyState'
import { RequestTenantModal } from './RequestTenantModal'
import { TenantRequestsTable } from './TenantRequestsTable'
import { CreateTenantModal } from '../../tenants/components/CreateTenantModal'
import { getAllTenantRequests, getMyTenantRequests } from '../services/tenant-request-api'
import type { TenantRequest, TenantRequestStatus } from '../types/tenant-request.types'

export function TenantRequestsList() {
  const { user } = useAuth()
  const [requests, setRequests] = useState<TenantRequest[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [initialLoad, setInitialLoad] = useState(true)
  const [editRequest, setEditRequest] = useState<TenantRequest | null>(null)
  const [createWorkspaceRequest, setCreateWorkspaceRequest] = useState<TenantRequest | null>(null)
  const [searchTerm, setSearchTerm] = useState('')

  // Derive permissions from JWT claims
  const isSysAdminUser = isSysAdmin(user)
  const isWorkspaceAdminUser = isWorkspaceAdmin(user)
  const isWorkspaceApproverUser = isWorkspaceApprover(user)

  // workspace_admin and workspace_approver can view all requests
  const canViewAllRequests = isSysAdminUser || isWorkspaceAdminUser || isWorkspaceApproverUser

  // workspace_approver can approve/reject requests
  const canApprove = isSysAdminUser || isWorkspaceApproverUser

  // The RequestTenantModal further restricts which workspace types each role can pick
  // (see getAllowedWorkspaceTypes — Sales=demo+trial, Consultant=uat, Finance=new).
  const canRequest = isSysAdminUser || isWorkspaceAdminUser || isWorkspaceApproverUser || isAnyRequester(user)

  // Default tab based on permissions
  const [activeTab, setActiveTab] = useState<string>(canViewAllRequests ? 'all' : 'my')

  const filteredRequests = useMemo(() => {
    if (!searchTerm.trim()) return requests

    const search = searchTerm.toLowerCase().trim()
    return requests.filter(
      request =>
        request.tenantCode?.toLowerCase().includes(search) ||
        request.workspaceCode?.toLowerCase().includes(search) ||
        request.workspaceName?.toLowerCase().includes(search) ||
        request.ownerEmail?.toLowerCase().includes(search) ||
        request.partnerCode?.toLowerCase().includes(search)
    )
  }, [requests, searchTerm])

  const loadRequests = async (status?: TenantRequestStatus) => {
    try {
      setLoading(true)
      setError(null)
      let data: TenantRequest[]

      if (canViewAllRequests && activeTab !== 'my') {
        data = await getAllTenantRequests(status)
      } else {
        data = await getMyTenantRequests()
        if (status) {
          data = data.filter(r => r.status === status)
        }
      }

      setRequests(data)
    } catch (err) {
      console.error('Failed to load requests:', err)
      setError(err as Error)
    } finally {
      setLoading(false)
      setInitialLoad(false)
    }
  }

  useEffect(() => {
    const status = activeTab === 'all' || activeTab === 'my' ? undefined : (activeTab as TenantRequestStatus)
    loadRequests(status)
  }, [activeTab])

  const handleTabChange = (key: string) => {
    setActiveTab(key)
  }

  const tabItems = [
    ...(canViewAllRequests
      ? [
          { label: 'All requests', key: 'all' },
          { label: 'Pending', key: 'p' },
          { label: 'Approved', key: 'a' },
          { label: 'My requests', key: 'my' }
        ]
      : [{ label: 'My requests', key: 'my' }])
  ]

  if (error) {
    return (
      <Alert
        message="Error loading workspace requests"
        description={error.message}
        type="error"
        showIcon
        action={
          <Button size="small" onClick={() => loadRequests()}>
            Retry
          </Button>
        }
      />
    )
  }

  if (initialLoad && loading) {
    return <Loading />
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 112px)' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '16px',
          flexShrink: 0
        }}
      >
        <Space size="middle">
          <Input
            placeholder="Search by tenant code, workspace code, name or email..."
            prefix={<SearchOutlined />}
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            style={{ width: 350 }}
            allowClear
          />
          <Button icon={<ReloadOutlined />} onClick={() => loadRequests()} loading={loading}>
            Refresh
          </Button>
          {canRequest && (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalOpen(true)}>
              Request workspace
            </Button>
          )}
        </Space>
      </div>

      <Tabs activeKey={activeTab} items={tabItems} onChange={handleTabChange} style={{ flexShrink: 0 }} />

      <div style={{ flex: 1, minHeight: 0 }}>
        {filteredRequests.length === 0 && !loading ? (
          <EmptyState
            description={
              searchTerm ? `No requests found matching "${searchTerm}"` : 'No requests found for this filter'
            }
          />
        ) : (
          <TenantRequestsTable
            requests={filteredRequests}
            loading={loading}
            onRefresh={loadRequests}
            onEdit={request => {
              setEditRequest(request)
              setModalOpen(true)
            }}
            onCreateWorkspace={request => setCreateWorkspaceRequest(request)}
            canApprove={canApprove}
          />
        )}
      </div>

      <RequestTenantModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false)
          setEditRequest(null)
        }}
        onSuccess={() => loadRequests()}
        editRequest={editRequest}
      />

      <CreateTenantModal
        open={!!createWorkspaceRequest}
        request={createWorkspaceRequest}
        onClose={() => setCreateWorkspaceRequest(null)}
        onSuccess={() => {
          loadRequests()
          setCreateWorkspaceRequest(null)
        }}
      />
    </div>
  )
}
